import { useCallback, useState } from 'react'
import { Form, Upload, message } from 'antd'
import type { FormInstance } from 'antd'
import { Upload as UploadIcon, XCircle, Loader2 } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Select } from '@/components/ui/select'
import { MediaPreviewModal } from '@/pages/events/MediaPreviewModal'
import { getMailTemplateFunctions } from '@/api/mail-templates.api'
import { fetchUnits } from '@/api/units.api'
import { uploadMultipartFile } from '@/api/file.api'
import { RichTextEditor } from '@/components/ui/rich-text-editor'

const PILL_INPUT =
  '!bg-white disabled:!bg-gray-100 disabled:hover:!bg-gray-100 disabled:!text-gray-500 disabled:!cursor-not-allowed rounded-full'

export interface MailTemplateFormValues {
  id?: string
  unitId: string
  unitName?: string
  name: string
  functionId: number
  title: string
  content?: string
  attachedFile?: any[]
  receiveMail?: string
}

interface MailTemplateFormProps {
  form: FormInstance<MailTemplateFormValues>
  mode: 'create' | 'update'
  showUnitColumn: boolean
  onSubmit: (values: MailTemplateFormValues) => void
}

const normFile = (e: any) => {
  if (Array.isArray(e)) return e
  return e?.fileList
}

export function MailTemplateForm({
  form,
  mode,
  showUnitColumn,
  onSubmit,
}: MailTemplateFormProps) {
  const [previewFile, setPreviewFile] = useState<{
    url: string
    name: string
  } | null>(null)
  const attachedFileList = Form.useWatch('attachedFile', form)
  const unitId = Form.useWatch('unitId', form)

  const { data: functionsData } = useQuery({
    queryKey: ['mail-template-functions'],
    queryFn: getMailTemplateFunctions,
  })
  const functionOptions =
    functionsData?.map((f) => ({ value: f.value, label: f.label })) || []

  const loadUnits = useCallback(async ({ page, signal, searchText }: any) => {
    const response = await fetchUnits(
      page,
      10,
      { keySearch: searchText?.trim() || undefined, status: 1 },
      signal,
    )
    const items = Array.isArray(response.items) ? response.items : []
    const hasNextPage = items.length >= 10
    return {
      options: items.map((unit: any) => ({ value: unit.id, label: unit.name })),
      hasNextPage,
      nextPage: hasNextPage ? page + 1 : undefined,
    }
  }, [])

  const handleFinish = (values: MailTemplateFormValues) => {
    const trimmedValues = Object.fromEntries(
      Object.entries(values).map(([key, value]) => [
        key,
        typeof value === 'string' ? value.trim() : value,
      ]),
    ) as MailTemplateFormValues
    onSubmit(trimmedValues)
  }

  const handleCustomUpload = async ({ file, onSuccess, onError }: any) => {
    try {
      const uploadedId = await uploadMultipartFile(file as File, 'media-file')

      file.id = uploadedId
      file.uploadedFileName = file.name || file.originFileObj?.name
      file.response = { id: uploadedId, name: file.name }

      onSuccess({ id: uploadedId, name: file.name }, file)
    } catch (error) {
      message.error('Tải tệp lên thất bại!')
      onError(error)
    }
  }

  const handlePreview = (e: React.MouseEvent, file: any) => {
    e.stopPropagation()
    e.preventDefault()
    let url = file.url || file.preview
    if (!url && file.originFileObj) {
      url = URL.createObjectURL(file.originFileObj)
    } else if (!url && file instanceof File) {
      url = URL.createObjectURL(file)
    }
    if (url) {
      const fileName =
        file.uploadedFileName ||
        file.name ||
        file.originFileObj?.name ||
        'attached_file'
      setPreviewFile({ url, name: fileName })
    }
  }

  const handleRemoveFile = (fileToRemove: any) => {
    const currentList = form.getFieldValue('attachedFile') || []
    const newList = currentList.filter(
      (f: any) => (f.uid || f.name) !== (fileToRemove.uid || fileToRemove.name),
    )
    form.setFieldValue('attachedFile', newList)
    form.validateFields(['attachedFile'])
  }

  return (
    <>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        requiredMark={(label, info) => (
          <>
            {label}
            {info.required && (
              <span className="text-destructive ml-1 text-red-500">*</span>
            )}
          </>
        )}
        className="space-y-6"
      >
        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Thiết lập nội dung</h3>

          {showUnitColumn && (
            <Form.Item
              name="unitId"
              label="Đơn vị"
              rules={[{ required: true, message: 'Vui lòng chọn đơn vị' }]}
            >
              <Select
                cacheKey="mail-template-units-select"
                initialPage={0}
                lazy
                size="large"
                placeholder="Chọn đơn vị"
                allowClear
                className="w-full h-10 !text-[#1E2F41] [&_input]:!text-[#1E2F41]"
                enableRemoteSearch
                initialOptions={
                  mode === 'update' && unitId
                    ? [{ value: unitId, label: form.getFieldValue('unitName') }]
                    : []
                }
                loadOptions={loadUnits}
                onLazyValueChange={(id) => form.setFieldsValue({ unitId: id })}
              />
            </Form.Item>
          )}

          <Form.Item
            name="name"
            label="Tên mẫu"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Vui lòng nhập tên mẫu',
              },
            ]}
          >
            <Input
              placeholder="Nhập tên mẫu"
              size="large"
              className={PILL_INPUT}
            />
          </Form.Item>

          <Form.Item
            name="functionId"
            label="Chức năng áp dụng"
            rules={[
              { required: true, message: 'Vui lòng chọn chức năng áp dụng' },
            ]}
          >
            <Select
              placeholder="Chọn chức năng"
              size="large"
              className={PILL_INPUT}
              options={functionOptions}
              onValueChange={(val) => {
                form.setFieldsValue({ functionId: val })
                form.validateFields(['functionId'])
              }}
            />
          </Form.Item>

          <Form.Item
            name="title"
            label="Tiêu đề"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Vui lòng nhập tiêu đề',
              },
            ]}
          >
            <Input
              placeholder="Nhập từ khóa"
              size="large"
              className={PILL_INPUT}
            />
          </Form.Item>

          <Form.Item
            name="attachedFile"
            label="Tệp đính kèm"
            valuePropName="fileList"
            getValueFromEvent={normFile}
            className="mb-2"
          >
            <Upload
              multiple
              accept=".mp4,.mov,.mkv,.avi,.webm,.mp3,.wav,.mav,.flac,.m4a,.aac,.jpg,.jpeg,.png,.webp,.svg,.pdf,.docx,.doc,.xls,.xlsx,.json,.txt,.csv,.apk"
              customRequest={handleCustomUpload}
              onChange={() => {
                setTimeout(() => form.validateFields(['attachedFile']), 50)
              }}
              showUploadList={false}
              className="[&_.ant-upload-select]:!w-full block"
            >
              <Button
                variant="outline"
                size="lg"
                className={`w-full flex items-center justify-between border-gray-300 overflow-hidden ${PILL_INPUT}`}
              >
                <span className="text-gray-400 truncate">Nhập URL</span>
                <UploadIcon size={16} />
              </Button>
            </Upload>
          </Form.Item>

          {attachedFileList && attachedFileList.length > 0 && (
            <div className="mb-6 max-h-[190px] overflow-y-auto pl-1">
              <div className="flex flex-wrap gap-2">
                {attachedFileList.map((file: any, index: number) => {
                  const fileName =
                    file.uploadedFileName ||
                    file.name ||
                    file.originFileObj?.name ||
                    `Tệp ${index + 1}`
                  const isUploading = file.status === 'uploading'

                  return (
                    <Button
                      key={file.uid || index}
                      variant="outline"
                      size="sm"
                      onClick={(e) => !isUploading && handlePreview(e, file)}
                      className="items-center rounded-full !border-[#CE7A58] !text-[#0E6DD2] hover:!border-[#CE7A58] max-w-[200px] h-8 gap-1.5"
                    >
                      {isUploading && (
                        <Loader2
                          size={14}
                          className="animate-spin text-[#CE7A58] shrink-0"
                        />
                      )}
                      <span className="truncate hover:underline">
                        {isUploading ? 'Đang tải lên...' : fileName}
                      </span>
                      {!isUploading && (
                        <XCircle
                          size={16}
                          className="shrink-0 text-[#1E2F4159] hover:text-[#CE7A58]"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleRemoveFile(file)
                          }}
                        />
                      )}
                    </Button>
                  )
                })}
              </div>
            </div>
          )}

          <Form.Item
            name="content"
            label="Nội dung"
            rules={[{ required: true, message: 'Vui lòng nhập nội dung mail' }]}
          >
            <RichTextEditor placeholder="Nhập ..." />
          </Form.Item>
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold">Kiểm tra gửi mail</h3>
          <Form.Item
            name="receiveMail"
            rules={[
              {
                required: true,
                whitespace: true,
                message: 'Vui lòng nhập mail nhận',
              },
            ]}
            label="Mail nhận"
          >
            <Input
              placeholder="Nhập địa chỉ email nhận"
              size="large"
              className={PILL_INPUT}
              type="email"
            />
          </Form.Item>
        </div>
      </Form>

      <MediaPreviewModal
        file={previewFile}
        onClose={() => setPreviewFile(null)}
      />
    </>
  )
}
